import { useLayoutEffect, useRef, type KeyboardEvent } from 'react';
import { ChevronDown, MessageSquareText, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  flattenRows,
  selectedRowKey,
  LIST_CHIPS,
  type ListChip,
  type ListRow,
  type ListSection,
} from '@/lib/conversationList';
import type { Selection } from '@/lib/conversationUrl';
import type { AgentNumberConfig } from '@/services/authService';
import ConversationListRow from './ConversationListRows';
import type { ResolveTicket } from './CallbackTicketRow';
import type { CallbackTicket } from '@/services/callbackTicketService';
import type { AgentLabel } from './ContextPanel';
import { useServerNow } from './useServerNow';

type Props = {
  sections: ListSection[];
  chip: ListChip;
  onChipChange: (chip: ListChip) => void;
  waitingCount: number;
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selection: Selection | null;
  onSelect: (row: ListRow) => void;
  agentLabel?: AgentLabel;
  showAgent: boolean;
  /** The search text the list was loaded for (for the empty text). */
  query: string;
  /** Agent lines of the salon; the filter shows only with more than one. */
  agents?: AgentNumberConfig[];
  agentFilter?: string;
  onAgentFilterChange?: (agentNumber: string) => void;
  /** „Erledigt“ of a Rückruf-Ticket (rejects when it failed). */
  onResolveTicket?: ResolveTicket;
};

function listRows(container: HTMLElement | null): HTMLElement[] {
  return container ? Array.from(container.querySelectorAll<HTMLElement>('[data-row]')) : [];
}

function focusRow(container: HTMLElement | null, index: number) {
  const rows = listRows(container);
  if (rows.length === 0) return;
  rows[Math.max(0, Math.min(rows.length - 1, index))].focus();
}

const EMPTY_TEXT: Record<ListChip, string> = {
  all: 'Noch keine Verläufe vorhanden',
  live: 'Gerade ist nichts live',
  waiting: 'Niemand wartet – alles beantwortet',
  taken: 'Kein Chat ist gerade übernommen',
  phone: 'Noch keine Telefonate',
  whatsapp: 'Noch keine WhatsApp-Chats',
};

/**
 * The left column of Verläufe: search, filter chips, what needs the salon now
 * (pinned), then contacts and calls by day. ↑/↓ move between rows, Enter opens.
 */
export function ConversationListPane({
  sections,
  chip,
  onChipChange,
  waitingCount,
  search,
  onSearchChange,
  loading,
  error,
  onRetry,
  selection,
  onSelect,
  agentLabel,
  showAgent,
  query,
  agents = [],
  agentFilter = '',
  onAgentFilterChange,
  onResolveTicket,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const now = useServerNow(30_000);
  const hasRows = sections.some((s) => s.rows.length > 0);
  const openKey = selectedRowKey(sections, selection);

  // A resolved ticket leaves the list and takes the focus with it: give it to
  // the row now at its place — unless the person has already moved on.
  const refocusRef = useRef<{ key: string; index: number } | null>(null);
  useLayoutEffect(() => {
    const pending = refocusRef.current;
    if (!pending || sections.some((s) => s.rows.some((r) => r.key === pending.key))) return;
    refocusRef.current = null;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    focusRow(listRef.current, pending.index);
  }, [sections]);

  const resolveTicket: ResolveTicket | undefined = onResolveTicket
    ? async (ticket: CallbackTicket, note: string) => {
        const key = `ticket:${ticket.id}`;
        // Render order = order of the [data-row] buttons.
        const index = flattenRows(sections).findIndex((row) => row.key === key);
        // Armed before the request: the answer may re-render the list before this resumes.
        refocusRef.current = { key, index: Math.max(0, index) };
        try {
          await onResolveTicket(ticket, note);
        } catch (error) {
          refocusRef.current = null;
          throw error;
        }
      }
    : undefined;

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only between rows: the note field and a row's own buttons keep their keys.
    if (!(e.target instanceof HTMLElement) || !e.target.hasAttribute('data-row')) return;
    const rows = listRows(listRef.current);
    const index = rows.indexOf(e.target);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusRow(listRef.current, index + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusRow(listRef.current, index <= 0 ? 0 : index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusRow(listRef.current, 0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusRow(listRef.current, rows.length - 1);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-shrink-0 space-y-3 px-3 pb-3 pt-3.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                focusRow(listRef.current, 0);
              }
            }}
            placeholder="Name, Nummer, Gesagtes …"
            aria-label="Verläufe durchsuchen"
            className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-brand/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 [&::-webkit-search-cancel-button]:hidden"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              aria-label="Suche löschen"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        {agents.length > 1 && onAgentFilterChange && (
          <div className="relative">
            <select
              value={agentFilter}
              onChange={(e) => onAgentFilterChange(e.target.value)}
              aria-label="Agent filtern"
              className="h-8 w-full appearance-none rounded-lg border border-border bg-background pl-3 pr-8 text-xs font-medium text-foreground focus-visible:border-brand/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
            >
              <option value="">Alle Agenten</option>
              {agents.map((agent) => (
                <option key={agent.phone_number} value={agent.phone_number}>
                  {agent.display_name || agent.phone_number}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        )}

        <div className="-mx-1 flex flex-wrap gap-1.5 px-1" role="group" aria-label="Filter">
          {LIST_CHIPS.map(({ id, label }) => {
            const active = chip === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => onChipChange(id)}
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-card',
                  active
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                )}
              >
                {label}
                {id === 'waiting' && waitingCount > 0 && (
                  <span
                    className={cn(
                      'min-w-[18px] rounded-full px-1 text-center text-[10.5px] font-bold tabular-nums',
                      active ? 'bg-background text-foreground' : 'bg-brand text-brand-foreground',
                    )}
                  >
                    <span className="sr-only">: </span>
                    {waitingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={listRef}
        onKeyDown={onListKeyDown}
        className="min-h-0 flex-1 overflow-y-auto px-2 pb-3"
        aria-busy={loading}
      >
        {loading && !hasRows ? (
          <div className="space-y-2 px-1 pt-1" aria-label="Verläufe werden geladen">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-1.5 py-1.5">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/5" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        ) : error && !hasRows ? (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            <p>{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              Erneut versuchen
            </button>
          </div>
        ) : !hasRows ? (
          <div className="flex flex-col items-center justify-center px-4 py-20 text-center text-muted-foreground">
            <MessageSquareText className="mb-2 h-9 w-9 opacity-25" aria-hidden="true" />
            <p className="text-sm">{query.trim() ? 'Keine Treffer' : EMPTY_TEXT[chip]}</p>
          </div>
        ) : (
          sections.map((section) => (
            <section key={section.id} aria-labelledby={`list-section-${section.id}`} className="pt-1.5">
              <h2
                id={`list-section-${section.id}`}
                className={cn(
                  'flex items-center gap-2 px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.1em]',
                  section.pinned ? 'text-brand-strong' : 'text-muted-foreground',
                )}
              >
                {section.pinned && <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />}
                {section.title}
                {section.pinned && <span className="font-medium tabular-nums">· {section.rows.length}</span>}
              </h2>
              <ul className="flex flex-col gap-0.5">
                {section.rows.map((row) => (
                  <li key={row.key}>
                    <ConversationListRow
                      row={row}
                      selected={row.key === openKey}
                      onSelect={() => onSelect(row)}
                      now={now}
                      agentLabel={agentLabel}
                      showAgent={showAgent}
                      onResolveTicket={resolveTicket}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

export default ConversationListPane;
